import { Product } from '@inventory/core';
import type { IProductRepository } from '@inventory/core';
import { BasePouchDBAdapter } from './BasePouchDBAdapter';
import type { BasePouchDocument } from '../types';

interface ProductPouchDocument extends BasePouchDocument {
  name: string;
  sku: string;
  description?: string;
  barcode?: string;
  category?: string;
  cost: { amount: number; currency: string };
  price: { amount: number; currency: string };
  minQuantity: { value: number; unit: string };
  maxQuantity?: { value: number; unit: string };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export class ProductPouchDBAdapter extends BasePouchDBAdapter<Product> implements IProductRepository {
  
  constructor(dbName: string) {
    super(dbName, 'product');
    this.createIndices();
  }

  async save(entity: Product): Promise<void> {
    const doc = this.toDocument(entity);
    try {
      const existing = await this.db.get(entity._id);
      await this.db.put({ ...doc, _rev: (existing as any)._rev });
    } catch (error) {
      if ((error as any).status === 404) {
        await this.db.put(doc);
      } else {
        throw this.handleError(error as any);
      }
    }
  }

  async delete(id: string): Promise<void> {
    try {
      const doc = await this.db.get(id);
      await this.db.remove(doc);
    } catch (error) {
      if ((error as any).name === 'not_found') {
        return;
      }
      throw error;
    }
  }

  private async createIndices(): Promise<void> {
    await this.createIndex(['sku']);
    await this.createIndex(['barcode']);
    await this.createIndex(['category']);
    await this.createIndex(['isActive']);
  }

  protected toDocument(product: Product): ProductPouchDocument {
    return {
      _id: product._id,
      type: product.type,
      version: product.version,
      name: product.name,
      sku: product.sku,
      description: product.description,
      barcode: product.barcode,
      category: product.category,
      cost: product.cost,
      price: product.price,
      minQuantity: product.minQuantity ?? { value: 0, unit: 'unit' },
      maxQuantity: product.maxQuantity,
      isActive: product.isActive,
      createdAt: product.createdAt.toISOString(),
      updatedAt: product.updatedAt.toISOString(),
    };
  }

  protected fromDocument(doc: ProductPouchDocument): Product {
    return Product.restore({
      _id: doc._id,
      type: doc.type,
      version: doc.version ?? 1,
      name: doc.name,
      sku: doc.sku,
      description: doc.description,
      barcode: doc.barcode,
      category: doc.category,
      cost: doc.cost,
      price: doc.price,
      minQuantity: doc.minQuantity,
      maxQuantity: doc.maxQuantity,
      isActive: doc.isActive,
      createdAt: new Date(doc.createdAt),
      updatedAt: new Date(doc.updatedAt),
    });
  }

  async findBySku(sku: string): Promise<Product | null> {
    console.log('ProductPouchDBAdapter.findBySku called with:', sku);
    const results = await this.query({ type: "product", sku: sku });
    return results[0] ?? null;
  }

  async findByBarcode(barcode: string): Promise<Product | null> {
    console.log('ProductPouchDBAdapter.findByBarcode called with:', barcode);
    const results = await this.query({ type: "product", barcode: barcode });
    return results[0] ?? null;
  }

  async findActive(): Promise<Product[]> {
    console.log('ProductPouchDBAdapter.findActive called');
    try {
      const result = await this.db.find({ selector: { type: "product", isActive: true } });
      console.log('PouchDB find result for active products:', result);
      const mappedDocs = result.docs.map(doc => this.fromDocument(doc as ProductPouchDocument));
      console.log({ mappedResults: mappedDocs });
      return mappedDocs;
    } catch (error) {
      console.error('Error querying active products from PouchDB:', error);
      throw error;
    }
  }
}